import { SvpSeason } from '@vgtv/api-client/lib/svp_season';
import type { Asset, Category } from '@schibsted-svp/svp-api-types';

import type { HttpClient } from './httpClient';
import { HttpClientOptions } from './httpClient';

type SeasonsAsset = Omit<Asset, '_embedded' | 'additional'> & {
  additional: Omit<Asset['additional'], 'tags'>;
};

export type AccessDefinitionEntity = {
  key: string;
  name: string;
};

type HalEmbedded<
  T extends Asset | Category | SvpSeason | AccessDefinitionEntity,
> = T extends Asset
  ? { assets: T[] }
  : T extends Category
    ? { categories: T[] }
    : T extends SvpSeason
      ? { seasons: T[] }
      : T extends AccessDefinitionEntity
        ? { accessDefinitions: T[] }
        : never;

interface HalLink {
  _links: {
    self: {
      href: string;
    };
    next?: {
      href: string;
    };
  };
}

interface HalResult<
  T extends Asset | Category | SvpSeason | AccessDefinitionEntity,
> extends HalLink {
  _embedded: HalEmbedded<T>;
  total_items: number;
}

const getNextPageParam = (url: string) => {
  const { searchParams } = new URL(url);

  if (searchParams.has('page')) {
    return Number(searchParams.get('page'));
  }

  return undefined;
};

const getEpisodes =
  (client: HttpClient, appName: string) =>
  async (
    {
      categoryId,
      seasonNumber,
      limit,
      page,
      sort,
    }: {
      categoryId: Category['id'];
      seasonNumber: number;
      limit?: number;
      page?: number;
      sort?: string;
    },
    options?: HttpClientOptions
  ): Promise<{ assets: Asset[]; nextPage?: number }> => {
    const searchParams = new URLSearchParams({ appName });

    if (limit) {
      searchParams.set('limit', String(limit));
    }

    if (sort) {
      searchParams.set('sort', sort);
    }

    if (page) {
      searchParams.set('page', String(page));
    }

    const data = await client(
      `/categories/${categoryId}/seasons/${seasonNumber}/assets`,
      { ...options, searchParams }
    ).then((r) => r.json() as Promise<HalLink & { assets: SeasonsAsset[] }>);

    searchParams.set(
      'filter',
      `id::${data.assets.map(({ id }) => id).join(',')}`
    );

    const {
      _embedded: { assets },
    } = await client('/search', { searchParams }).then(
      (r) => r.json() as Promise<HalResult<Asset>>
    );

    const nextPage = data._links.next?.href
      ? getNextPageParam(data._links.next.href)
      : undefined;

    return { assets, nextPage };
  };

const getSeasons =
  (client: HttpClient, appName: string) =>
  async (
    categoryId: Category['id'],
    options?: HttpClientOptions
  ): Promise<SvpSeason[]> => {
    const searchParams = new URLSearchParams({ appName });

    const response = await client(`/categories/${categoryId}/seasons`, {
      ...options,
      searchParams,
    });
    const data: HalResult<SvpSeason> = await response.json();

    return data._embedded.seasons;
  };

const getAccessDefinitions =
  (client: HttpClient, appName: string) =>
  async (options?: HttpClientOptions): Promise<AccessDefinitionEntity[]> => {
    const searchParams = new URLSearchParams({ appName });
    const response = await client(`/assets/access-definitions`, {
      ...options,
      searchParams,
    });

    const data: HalResult<AccessDefinitionEntity> = await response.json();

    return data._embedded.accessDefinitions;
  };

const getAsset =
  (client: HttpClient, appName: string) =>
  async (
    {
      assetId,
    }: {
      assetId: Asset['id'];
    },
    options?: HttpClientOptions
  ): Promise<Asset> => {
    const searchParams = new URLSearchParams({ appName });

    const response = await client(`/assets/${assetId}`, {
      ...options,
      searchParams,
    });

    return response.json();
  };

const seasonApi = (client: HttpClient, appName: string) => {
  return {
    getSeasons: getSeasons(client, appName),
    getEpisodes: getEpisodes(client, appName),
  };
};

const assetApi = (client: HttpClient, appName: string) => {
  return {
    getAsset: getAsset(client, appName),
    getAccessDefinitions: getAccessDefinitions(client, appName),
  };
};

export type SvpApiClient = ReturnType<typeof createSvpApi>;

export const createSvpApi = (client: HttpClient, appName: string) => {
  return {
    seasons: seasonApi(client, appName),
    assets: assetApi(client, appName),
  };
};
